import { useEffect, useRef } from 'react';
import {
  Animated,
  Easing,
  Pressable,
  StyleSheet,
  Text,
} from 'react-native';
import { webTapFloor } from '../theme';
import type { useTheme } from './theme';

// Empty-state card shown in the FocusPanel slot when no exercise is
// focused (timeline logger, Option B — Antenore 2026-06-02). At workout
// start the active card is intentionally empty and explains how to begin
// ("help included in the price"): the athlete reads the roster, sees the
// gym, and decides — rather than the app auto-picking. Also shown after
// removing the focused exercise (re-orient) and for a blank ad-hoc
// workout. The message is passed in by the screen (roster-present vs
// no-exercises-yet). Styled to match FocusPanel's `panel` card so the
// slot looks continuous when it swaps between the two.
//
// The add-exercise button is OPTIONAL, and that is the whole point (PE-22
// fetta E): the roster strip's fixed ＋ sits right above this card whenever
// there is a roster at all, and two controls announcing "Add exercise" in the
// same view are indistinguishable to a screen reader. So the button only
// renders in the one state the strip does not cover — a workout with no
// exercises yet, where the strip is not mounted and this card is the only way
// in.
export function EmptyFocusCard({
  message,
  addAction,
  flashOnAppear = false,
  theme,
}: {
  message: string;
  /**
   * The "add an exercise" button. Optional, and one object rather than two
   * loose props so half of it cannot be passed: omitted when the roster strip
   * is on screen, because its fixed ＋ already carries this action under this
   * same name. Same shape as ChoiceTray's `create`.
   */
  addAction?: { label: string; onPress: () => void };
  /** Pulse the card's border/background once when it first appears. Set true
   *  only for the workout-complete placeholder so the last-set → placeholder
   *  swap gets the same advance transition as set→set / exercise→exercise
   *  (the swap changes component, so FocusPanel's own flash can't carry it —
   *  Antenore 2026-06-08 smoke). */
  flashOnAppear?: boolean;
  theme: ReturnType<typeof useTheme>;
}) {
  const styles = makeStyles(theme);
  const p = theme.palette;

  // One-shot highlight on appear, mirroring FocusPanel's advance flash. Runs on
  // mount (the placeholder mounts fresh when it replaces FocusPanel on the last
  // log); flashOnAppear is stable per mount so the effect fires exactly once.
  const flash = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!flashOnAppear) return;
    flash.setValue(1);
    Animated.timing(flash, {
      toValue: 0,
      duration: 650,
      easing: Easing.out(Easing.quad),
      useNativeDriver: false,
    }).start();
  }, [flashOnAppear, flash]);
  const flashBg = flash.interpolate({
    inputRange: [0, 1],
    outputRange: [p.surface, p.brand100],
  });
  const flashBorder = flash.interpolate({
    inputRange: [0, 1],
    outputRange: [p.border, p.brand500],
  });

  return (
    <Animated.View
      style={[styles.card, { backgroundColor: flashBg, borderColor: flashBorder }]}
    >
      <Text style={styles.message}>{message}</Text>
      {addAction ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={addAction.label}
          onPress={addAction.onPress}
          hitSlop={8}
          style={({ pressed }) => [
            styles.addBtn,
            pressed && styles.addBtnPressed,
          ]}
        >
          <Text style={styles.addBtnText}>+ {addAction.label}</Text>
        </Pressable>
      ) : null}
    </Animated.View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      borderStyle: 'dashed',
      padding: s.s4,
      gap: s.s3,
      alignItems: 'center',
    },
    message: {
      color: p.ink2,
      fontSize: ty.sizes.base,
      textAlign: 'center',
      lineHeight: ty.sizes.base + 6,
    },
    addBtn: {
      // 40 + hitSlop 8 clears the floor on native, but RNW drops hitSlop, so on
      // web this was a 40pt target. It matters more since PE-22 fetta E: the
      // roster strip's ＋ does not cover the blank-workout state, which makes
      // this the ONE way into a new workout.
      minHeight: webTapFloor ?? 40,
      justifyContent: 'center',
      paddingHorizontal: s.s4,
      borderRadius: r.r2,
      backgroundColor: p.brand500,
    },
    addBtnPressed: { backgroundColor: p.brand600 },
    addBtnText: {
      color: p.inkOnBrand,
      fontSize: ty.sizes.base,
      fontWeight: '700',
    },
  });
}
